import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import type { StationDetailOut } from '@/api/stations';

interface Coords {
  lat: number;
  lng: number;
}

const FIT_PADDING_PX = 64;
const MIN_SINGLE_STATION_ZOOM = 15;

function validCoordsOf(station: StationDetailOut): Coords | null {
  const lat = Number(station.latitude);
  const lng = Number(station.longitude);
  if (
    station.latitude === null ||
    station.longitude === null ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return null;
  }
  return { lat, lng };
}

/**
 * Ajusta a câmera do mapa pra enquadrar todas as estações (+ a posição do
 * usuário, se houver) — mas só quando as estações carregam pela primeira vez
 * ou quando a geolocalização é concedida depois do mount. NUNCA no poll
 * silencioso de 10s da Home, senão o mapa recentraliza embaixo do dedo de
 * quem já está navegando nele.
 */
export function useFitBoundsToStations(
  mapId: string,
  stations: StationDetailOut[],
  userCoords: Coords | null
): void {
  const map = useMap(mapId);
  const hasFittedStationsRef = useRef(false);
  const hadCoordsRef = useRef(false);

  useEffect(() => {
    if (!map) return;

    const validStations = stations
      .map(validCoordsOf)
      .filter((c): c is Coords => c !== null);

    const stationsJustLoaded =
      !hasFittedStationsRef.current && validStations.length > 0;
    const coordsJustGranted = !hadCoordsRef.current && userCoords !== null;

    if (validStations.length > 0) hasFittedStationsRef.current = true;
    if (userCoords !== null) hadCoordsRef.current = true;

    if (!stationsJustLoaded && !coordsJustGranted) return;
    if (validStations.length === 0) return; // fallback center/zoom já cobre

    const bounds = new google.maps.LatLngBounds();
    for (const c of validStations) bounds.extend(c);
    if (userCoords) bounds.extend(userCoords);

    map.fitBounds(bounds, FIT_PADDING_PX);

    // fitBounds com 1 único ponto (estação isolada, sem posição do usuário)
    // zoom quase no talo — trava num mínimo legível.
    if (validStations.length === 1 && !userCoords) {
      const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        if ((map.getZoom() ?? 0) > MIN_SINGLE_STATION_ZOOM) {
          map.setZoom(MIN_SINGLE_STATION_ZOOM);
        }
      });
      return () => google.maps.event.removeListener(listener);
    }
  }, [map, stations, userCoords]);
}
